// types.ts (archivo separado para los tipos)
export interface ApiConfig {
  url: string;
  token?: string;
  refreshToken?: string;
  TenancyName?: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

// apiService.ts
export function get<T = any>(
  apiConfig: ApiConfig,
  apiControllerCall: string,
  requestPayload?: any,
  ignoreParseJson?: boolean,
): Promise<T>
{
  return callFetch<T>(apiConfig, apiControllerCall, 'GET', requestPayload, ignoreParseJson);
}

export function post<T = any>(
  apiConfig: ApiConfig,
  apiControllerCall: string,
  requestPayload?: any,
  ignoreParseJson?: boolean,
): Promise<T>
{
  return callFetch<T>(apiConfig, apiControllerCall, 'POST', requestPayload, ignoreParseJson);
}

export function put<T = any>(
  apiConfig: ApiConfig,
  apiControllerCall: string,
  requestPayload?: any,
  ignoreParseJson?: boolean,
): Promise<T>
{
  return callFetch<T>(apiConfig, apiControllerCall, 'PUT', requestPayload, ignoreParseJson);
}

/**
 * Provide main functions for make call to any API
 */
export function callFetch<T = any>(
  apiConfig: ApiConfig,
  apiControllerCall: string,
  method?: HttpMethod,
  requestPayload?: any,
  ignoreParseJson?: boolean,
  isMultipart?: boolean,
): Promise<T>
{
  const rawUrl =
    apiConfig.url.startsWith('http://') || apiConfig.url.startsWith('https://')
      ? apiConfig.url
      : `http://${apiConfig.url}`;
  const baseUrl = rawUrl.endsWith('/') ? rawUrl : `${rawUrl}/`;

  let token = window.localStorage.getItem('C2_access_token');
  let refreshToken = window.localStorage.getItem('C2_refresh_token');

  if (!token || token.length === 0)
  {
    token = apiConfig.token || '';
  }

  if (!refreshToken || refreshToken.length === 0)
  {
    refreshToken = apiConfig.refreshToken || '';
  }

  const config: RequestInit = {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json;charset=UTF-8',
    },
  };

  if (method)
  {
    config.method = method;
  }

  if (isMultipart)
  {
    config.headers = {
      Authorization: `Bearer ${token}`,
    };

    if (requestPayload)
    {
      config.body = requestPayload;
    }
  }
  else
  {
    if (requestPayload)
    {
      config.body = JSON.stringify(requestPayload);
    }
  }

  const goToLogin = (): void =>
  {
    const host = `${window.location.protocol}//${window.location.host}/`;
    if (window.location.href.indexOf('/Chat/') > 0)
    {
      window.location.href = `${host}Chat/#/Login`;
    }
    else
    {
      window.location.href = `${host}Account/Login/#/`;
    }
    window.localStorage.clear();
  };

  const validateErrorRequest = async (
    errorPromise: Promise<any>,
    endpoint: string,
    _config: RequestInit,
    is401: boolean,
  ): Promise<T | void> =>
  {
    try
    {
      const rejection = await errorPromise;

      const isAuthError =
        (rejection.exceptionType &&
          rejection.exceptionType === 'Abp.Authorization.AbpAuthorizationException' &&
          rejection.exceptionMessage === 'Current user did not login to the application!') ||
        is401;

      if (isAuthError)
      {
        const tenancyName = apiConfig.TenancyName;

        if (refreshToken && tenancyName)
        {
          const data = `grant_type=refresh_token&refresh_token=${refreshToken}&tenancyName=${tenancyName}&Client_id=CorgeeWeb`;

          try
          {
            const response = await fetch(`${baseUrl}token`, {
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              method: 'POST',
              body: data,
            });

            if (!response.ok)
            {
              throw new Error(response.statusText);
            }

            const tokenData: TokenResponse = ignoreParseJson
              ? (response as any)
              : await response.json();

            window.localStorage.setItem('C2_refresh_token', tokenData.refresh_token);
            window.localStorage.setItem('C2_access_token', tokenData.access_token);

            // Actualizar el header con el nuevo token
            (_config.headers as Record<string, string>)['Authorization'] =
              `Bearer ${tokenData.access_token}`;

            const retryResponse = await fetch(endpoint, _config);

            if (retryResponse.ok)
            {
              return ignoreParseJson ? (retryResponse as any) : await retryResponse.json();
            }
          }
          catch (error)
          {
            console.error('Error refreshing token:', error);
            goToLogin();
          }
        }
        else
        {
          goToLogin();
        }
      }
    }
    catch (error)
    {
      console.error('Error validating request:', error);
      throw error;
    }
  };

  return new Promise<T>((resolve, reject) =>
  {
    fetch(`${baseUrl}${apiControllerCall}`, config)
      .then(response =>
      {
        if (response.ok)
        {
          if (ignoreParseJson)
          {
            return { data: response, validateError: false };
          }
          else
          {
            return { data: response.json(), validateError: false };
          }
        }
        else
        {
          if (response.status === 500 || response.status === 401)
          {
            return {
              data: response.json(),
              validateError: true,
              is401: response.status === 401,
            };
          }
          else
          {
            return { data: response, error: true, validateError: false };
          }
        }
      })
      .then(async result =>
      {
        if (result.validateError)
        {
          const validatedData = await validateErrorRequest(
            result.data as Promise<any>,
            `${baseUrl}${apiControllerCall}`,
            config,
            result.is401 || false,
          );
          if (validatedData)
          {
            resolve(validatedData);
          }
        }
        else if (result.data)
        {
          const resolvedData = result.data instanceof Promise ? await result.data : result.data;
          resolve(resolvedData);
        }
        else
        {
          resolve(undefined as any);
        }
      })
      .catch(error =>
      {
        console.error('Fetch error:', error);
        reject(error);
      });
  });
}
